import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./css/login.css";
import { apiUrl } from "./config/api";

async function readJsonResponse(response) {
  const rawResult = await response.text();
  if (!rawResult) return {};
  try {
    return JSON.parse(rawResult);
  } catch (_err) {
    return {};
  }
}

function ResetPassword() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get("token") || "";
    setToken(resetToken);

    if (!resetToken) {
      setChecking(false);
      setValid(false);
      setError("El enlace es inválido o expiró.");
      return;
    }

    const checkToken = async () => {
      setChecking(true);
      setError("");

      try {
        const url = new URL(apiUrl("/auth/password-reset/status"));
        url.searchParams.set("token", resetToken);
        const response = await fetch(url.toString());
        const result = await readJsonResponse(response);

        if (!response.ok || !result.success) {
          throw new Error(result.message || "El enlace es inválido o expiró.");
        }

        setEmail(result.email || "");
        setValid(true);
      } catch (err) {
        setValid(false);
        setError(err.message || "El enlace es inválido o expiró.");
      } finally {
        setChecking(false);
      }
    };

    checkToken();
  }, []);

  const validatePassword = () => {
    if (password.length < 8) return "La contraseña debe tener al menos 8 caracteres.";
    if (!/[A-Za-z]/.test(password)) return "La contraseña debe incluir al menos una letra.";
    if (!/[0-9]/.test(password)) return "La contraseña debe incluir al menos un número.";
    if (password !== confirmPassword) return "Las contraseñas no coinciden.";
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validatePassword();

    if (validationError) {
      setError(validationError);
      setMessage("");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(apiUrl("/auth/password-reset/confirm"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, confirmPassword }),
      });
      const result = await readJsonResponse(response);

      if (!response.ok || !result.success) {
        throw new Error(result.message || "No se pudo actualizar la contraseña.");
      }

      setPassword("");
      setConfirmPassword("");
      setMessage("Contraseña actualizada. Redirigiendo al login...");
      setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      setError(err.message || "No se pudo actualizar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-page__hero" aria-labelledby="reset-title">
        <img src="/images/logowp.png" className="login-page__logo" alt="Kakao Apps" />
        <div className="login-page__hero-copy">
          <div className="login-page__badge">Kakao Apps</div>
          <h1 id="reset-title">Nueva contraseña</h1>
          <p>Crea una contraseña segura para recuperar tu acceso.</p>
        </div>
      </section>

      <section className="login-container" aria-label="Nueva contraseña">
        {checking ? (
          <div className="login-message">Validando enlace...</div>
        ) : valid ? (
          <form className="signin-form" onSubmit={handleSubmit} autoComplete="off">
            {email ? <p className="signin-form__helper">Cuenta: {email}</p> : null}
            <div className="signin-form__fields">
              <label className="signin-field" htmlFor="new-password">
                <span>Nueva contraseña</span>
                <div className="signin-field__input">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="new-password"
                    required
                    placeholder="Nueva contraseña"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
              </label>

              <label className="signin-field" htmlFor="confirm-password">
                <span>Confirmar contraseña</span>
                <div className="signin-field__input">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirm-password"
                    required
                    placeholder="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="signin-field__toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <svg className="signin-field__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M12 5c5.5 0 9.9 3.7 11.4 8-1.5 4.3-5.9 8-11.4 8S2.1 17.3.6 13C2.1 8.7 6.5 5 12 5Zm0 2C7.4 7 3.7 9.6 2.3 13 3.7 16.4 7.4 19 12 19s8.3-2.6 9.7-6c-1.4-3.4-5.1-6-9.7-6Zm0 2.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"
                    />
                  </svg>
                  <span className="signin-field__toggle-text">
                    {showPassword ? "Hide password" : "View password"}
                  </span>
                </button>
              </label>
            </div>

            <p className="signin-form__hint">
              Usa al menos 8 caracteres e incluye letras y números.
            </p>

            <button className="primary-button" type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Actualizar contraseña"}
            </button>
          </form>
        ) : (
          <p className="login-message login-message--error">{error}</p>
        )}

        {message ? <div id="error-message" className="login-message login-message--success">{message}</div> : null}
        {error && valid ? <div id="error-message" className="login-message login-message--error">{error}</div> : null}
        <p className="signin-form__helper">
          <Link className="signin-form__link" to="/">Volver al login</Link>
        </p>
      </section>
    </main>
  );
}

export default ResetPassword;
